/**
 * Admin API Service
 * Handles all API calls for admin dashboard operations
 * Uses HttpOnly cookie authentication (no token parameter needed)
 * Consistent with main apiService.js architecture
 */

import axios from 'axios';
import toast from 'react-hot-toast';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Default timeout for API calls - increased for admin endpoints that may need more time
// Admin operations often involve complex queries and data processing
const DEFAULT_TIMEOUT = 30000; // 30 seconds for admin endpoints

// ✅ Create axios instance with credentials enabled (same as main apiService)
const adminApiClient = axios.create({
  baseURL: API_BASE,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // ✅ CRITICAL: Send HttpOnly cookies with every request
});

// ✅ Request interceptor - cookies are automatic, no token needed
adminApiClient.interceptors.request.use(
  (config) => {
    logger.debug(`Admin API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    logger.error('Admin API Request Error:', error);
    return Promise.reject(error);
  }
);

// ✅ Response interceptor with error handling
adminApiClient.interceptors.response.use(
  (response) => {
    logger.debug(`Admin API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    return response;
  },
  (error) => {
    // Handle authentication errors
    if (error.response?.status === 401 || error.response?.status === 403) {
      logger.error('Admin authentication expired - cookie invalid or expired');
      toast.error('Session expired. Please login again.');
      // Redirect to login
      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);
      return Promise.reject({ message: 'UNAUTHORIZED', originalError: error });
    }

    // Handle network errors (no response received) - timeouts, content length mismatches, etc.
    if (!error.response) {
      const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      const isContentLengthMismatch = error.message?.includes('ERR_CONTENT_LENGTH_MISMATCH') || 
                                     error.code === 'ERR_CONTENT_LENGTH_MISMATCH';
      
      // Log timeout errors at debug level to reduce console noise
      if (isTimeout) {
        logger.debug(`⏱️ Admin API timeout: ${error.config?.method?.toUpperCase()} ${error.config?.url}`);
      } 
      // Log content length mismatch at debug level - this is often a transient network issue
      else if (isContentLengthMismatch) {
        logger.debug(`⚠️ Admin API content length mismatch (transient): ${error.config?.method?.toUpperCase()} ${error.config?.url}`);
      }
      // Only warn for other network errors
      else {
        logger.warn('⚠️ Admin API network error - no response received:', error.message);
      }
      
      return Promise.reject({
        isNetworkError: true,
        isTimeout: isTimeout,
        isContentLengthMismatch: isContentLengthMismatch,
        message: error.message || 'Network error',
        originalError: error
      });
    }

    // Handle other errors - DON'T show toast here, let the component handle it
    const status = error.response?.status;
    const data = error.response?.data;

    // Log errors but don't show toast (components will handle success/error toasts)
    logger.error('Admin API Response Error:', status, data || error.message);
    return Promise.reject(error);
  }
);

// ✅ Generic admin API call wrapper (matches main apiService pattern)
const adminApiCall = async (method, endpoint, data = null, config = {}) => {
  try {
    const response = await adminApiClient({
      method,
      url: endpoint,
      data,
      ...config
    });

    // Return response data directly or wrap it
    if (response.data && typeof response.data === 'object' && 'success' in response.data) {
      return response.data;
    }

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error) {
    const errorDetails = {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      url: error.config?.url,
      method: error.config?.method
    };
    logger.error('❌ Admin API Error:', JSON.stringify(errorDetails, null, 2));

    // Handle unauthorized separately (already redirected)
    if (error.message === 'UNAUTHORIZED') {
      throw error;
    }

    const status = error.response?.status;
    const responseData = error.response?.data;

    // Show appropriate error toast based on status
    // Use toast.id to prevent duplicate toasts for the same error
    const errorMessage = responseData?.detail?.message || responseData?.message || error.message || 'An error occurred';
    const toastId = `admin-error-${status}-${errorMessage.substring(0, 50)}`;
    
    if (status === 409) {
      toast.error(responseData?.detail?.message || responseData?.message || 'Duplicate entry found', { id: toastId });
    } else if (status === 404) {
      // Only show 404 toast if it's not a GET request (GET 404s are often expected)
      const isGetRequest = error.config?.method?.toLowerCase() === 'get';
      if (!isGetRequest) {
        toast.error(responseData?.detail?.message || responseData?.message || 'Resource not found', { id: toastId });
      }
    } else if (status === 422) {
      // Validation error - handle both FastAPI standard format and custom format
      if (responseData?.errors && Array.isArray(responseData.errors)) {
        // Custom error format from backend: { errors: [{field, message, type}], message: "..." }
        const errorMessages = responseData.errors.map(err => {
          const field = err.field ? err.field.replace('body -> ', '') : 'field';
          const msg = err.message || 'Invalid value';
          return `${field}: ${msg}`;
        }).join('\n');
        toast.error(errorMessages, { duration: 5000, id: toastId }); // Show longer for multiple errors
      } else if (responseData?.detail && Array.isArray(responseData.detail)) {
        // FastAPI standard validation error format: { detail: [{loc, msg, type}] }
        const errorMessages = responseData.detail.map(err => {
          const field = err.loc ? err.loc[err.loc.length - 1] : 'field';
          const msg = err.msg || 'Invalid value';
          return `${field}: ${msg}`;
        }).join('\n');
        toast.error(errorMessages, { duration: 5000, id: toastId });
      } else {
        // Fallback to generic message
        const errorMsg = responseData?.detail?.message || responseData?.message || 'Validation error';
        toast.error(errorMsg, { id: toastId });
      }
    } else if (status === 400) {
      toast.error(responseData?.detail?.message || responseData?.message || 'Bad request', { id: toastId });
    } else if (status >= 500) {
      toast.error(responseData?.detail?.message || responseData?.message || 'Server error. Please try again later.', { id: toastId });
    } else if (status) {
      // Generic error (only show if we have a status code)
      toast.error(responseData?.message || error.message || 'An error occurred', { id: toastId });
    }

    // Return error object for component handling
    return {
      success: false,
      data: null,
      error: responseData?.detail?.message || responseData?.message || error.message || 'Request failed'
    };
  }
};

// ============================================================================
// QUESTIONS API
// ============================================================================

/**
 * Fetch all questions with filters
 * @param {Object} filters - Filter parameters (no token needed)
 */
export const fetchQuestions = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.industry_id) params.append('industry_id', filters.industry_id);
  if (filters.region) params.append('region', filters.region);
  if (filters.assessment_type) params.append('assessment_type', filters.assessment_type);
  if (filters.category) params.append('category', filters.category);
  if (filters.search) params.append('search', filters.search);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset !== undefined) params.append('offset', filters.offset);

  const queryString = params.toString();
  const endpoint = `/admin/questions${queryString ? `?${queryString}` : ''}`;
  
  return await adminApiCall('GET', endpoint);
};

/**
 * Create a new question
 */
export const createQuestion = async (questionData) => {
  const result = await adminApiCall('POST', '/admin/questions', questionData);
  if (result.success) {
    toast.success('Question created successfully!');
  }
  return result;
};

/**
 * Get single question by ID
 */
export const fetchQuestionById = async (questionId) => {
  return await adminApiCall('GET', `/admin/questions/${questionId}`);
};

/**
 * Update a question
 */
export const updateQuestion = async (questionId, questionData) => {
  const result = await adminApiCall('PUT', `/admin/questions/${questionId}`, questionData);
  if (result.success) {
    toast.success('Question updated successfully!');
  }
  return result;
};

/**
 * Delete a question
 */
export const deleteQuestion = async (questionId) => {
  const result = await adminApiCall('DELETE', `/admin/questions/${questionId}`);
  if (result.success) {
    toast.success('Question deleted successfully!');
  }
  return result;
};

/**
 * Detect duplicate questions
 */
export const detectDuplicateQuestions = async (threshold = 0.85, region = null) => {
  const params = new URLSearchParams({ threshold: threshold.toString() });
  if (region) params.append('region', region);
  
  return await adminApiCall('GET', `/admin/questions/duplicates/detect?${params.toString()}`);
};

// ============================================================================
// INDUSTRIES API
// ============================================================================

/**
 * Fetch all industries with optional sub-industries
 * Uses admin /api/admin/industries endpoint which includes question_count and sub_industries
 */
export const fetchIndustries = async (includeSubIndustries = true, region = null) => {
  try {
    // Use admin industries endpoint which includes question_count and sub_industries
    const params = new URLSearchParams();
    if (region) {
      params.append('region', region);
    }
    const queryString = params.toString();
    const endpoint = `/admin/industries${queryString ? `?${queryString}` : ''}`;
    
    logger.log(`🔍 Fetching industries from: ${endpoint}`);
    const response = await adminApiClient.get(endpoint);
    
    logger.log('📊 Industries API response:', response.data);
    
    // Handle response format: backend returns { success: true, industries: [...] }
    let industries = [];
    if (response.data && response.data.success && response.data.industries) {
      industries = response.data.industries;
    } else if (response.data && Array.isArray(response.data.industries)) {
      industries = response.data.industries;
    } else if (response.data && Array.isArray(response.data.data)) {
      industries = response.data.data;
    } else if (Array.isArray(response.data)) {
      industries = response.data;
    }
    
    logger.log(`✅ Parsed ${industries.length} industries from response`);
    
    return {
      success: true,
      industries: Array.isArray(industries) ? industries : [],
      error: null
    };
  } catch (error) {
    const errorDetails = {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      url: error.config?.url,
      method: error.config?.method
    };
    logger.error('❌ Error fetching industries:', JSON.stringify(errorDetails, null, 2));
    
    // Handle network errors gracefully
    if (error.isNetworkError) {
      if (error.isTimeout || error.isContentLengthMismatch) {
        logger.debug('Industries fetch timeout/mismatch - will retry on next action');
      } else {
        logger.error('Error fetching industries:', error);
      }
    }
    
    // Don't show toast for this - error handling is done in component
    return {
      success: false,
      industries: [],
      error: error.response?.data?.detail || error.message || 'Failed to fetch industries'
    };
  }
};

/**
 * Create a new industry
 */
export const createIndustry = async (industryData) => {
  const result = await adminApiCall('POST', '/admin/industries', industryData);
  if (result.success) {
    toast.success('Industry created successfully!');
  }
  return result;
};

/**
 * Update an industry
 */
export const updateIndustry = async (industryId, industryData) => {
  const result = await adminApiCall('PUT', `/admin/industries/${industryId}`, industryData);
  if (result.success) {
    toast.success('Industry updated successfully!');
  }
  return result;
};

/**
 * Delete an industry
 */
export const deleteIndustry = async (industryId) => {
  const result = await adminApiCall('DELETE', `/admin/industries/${industryId}`);
  if (result.success) {
    toast.success('Industry deleted successfully!');
  }
  return result;
};

// ============================================================================
// SUB-INDUSTRIES API
// ============================================================================

/**
 * Fetch all sub-industries
 * Uses public /api/sub-industries-by-major or admin /api/sub-industries endpoint
 */
export const fetchSubIndustries = async (industryId = null, majorIndustry = null, region = 'naics') => {
  try {
    let endpoint;
    
    if (majorIndustry) {
      // Prefer the new public endpoint by major industry name and region
      endpoint = `/sub-industries-by-major?major_industry=${encodeURIComponent(majorIndustry)}&region=${region}`;
    } else if (industryId) {
      // Use the admin endpoint by industry_id (requires admin auth)
      endpoint = `/admin/subindustries?industry_id=${industryId}`;
    } else {
      // Get all sub-industries (requires admin auth)
      endpoint = '/admin/subindustries';
    }
    
    logger.log(`🔍 Fetching sub-industries from: ${endpoint}`);
    const response = await adminApiClient.get(endpoint);
    
    logger.log('📊 Sub-industries API response:', response.data);
    
    // Handle response format: backend returns { success: true, sub_industries: [...] }
    let subIndustries = [];
    if (response.data && response.data.success && response.data.sub_industries) {
      subIndustries = response.data.sub_industries;
    } else if (response.data && Array.isArray(response.data.sub_industries)) {
      subIndustries = response.data.sub_industries;
    } else if (response.data && Array.isArray(response.data.data)) {
      subIndustries = response.data.data;
    } else if (Array.isArray(response.data)) {
      subIndustries = response.data;
    }
    
    logger.log(`✅ Parsed ${subIndustries.length} sub-industries from response`);
    
    return {
      success: true,
      sub_industries: Array.isArray(subIndustries) ? subIndustries : [],
      error: null
    };
  } catch (error) {
    const errorDetails = {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      url: error.config?.url,
      method: error.config?.method
    };
    logger.error('❌ Error fetching sub-industries:', JSON.stringify(errorDetails, null, 2));
    // Don't show toast for 404s on GET requests - return empty array silently
    if (error.response?.status === 404 && error.config?.method?.toLowerCase() === 'get') {
      return {
        success: false,
        sub_industries: [],
        error: null // Don't treat 404 as error for GET requests
      };
    }
    // For other errors, return empty array without showing toast (component will handle)
    return {
      success: false,
      sub_industries: [],
      error: error.response?.data?.detail || error.message || 'Failed to fetch sub-industries'
    };
  }
};

/**
 * Create a new sub-industry
 */
export const createSubIndustry = async (subIndustryData) => {
  const { industry_id, ...bodyData } = subIndustryData;
  
  if (!industry_id) {
    toast.error('Parent industry is required');
    throw new Error('Parent industry is required');
  }
  
  const result = await adminApiCall('POST', `/admin/subindustries?industry_id=${industry_id}`, bodyData);
  if (result.success) {
    toast.success('Sub-industry created successfully!');
  }
  return result;
};

/**
 * Update a sub-industry
 */
export const updateSubIndustry = async (subIndustryId, subIndustryData) => {
  const result = await adminApiCall('PUT', `/admin/subindustries/${subIndustryId}`, subIndustryData);
  if (result.success) {
    toast.success('Sub-industry updated successfully!');
  }
  return result;
};

/**
 * Delete a sub-industry
 */
export const deleteSubIndustry = async (subIndustryId) => {
  const result = await adminApiCall('DELETE', `/admin/subindustries/${subIndustryId}`);
  if (result.success) {
    toast.success('Sub-industry deleted successfully!');
  }
  return result;
};

// ============================================================================
// STATS API
// ============================================================================

/**
 * Fetch admin dashboard statistics
 */
export const fetchAdminStats = async () => {
  return await adminApiCall('GET', '/admin/stats');
};

// ============================================================================
// EXPORT DEFAULT SERVICE OBJECT
// ============================================================================

export default {
  // Questions
  fetchQuestions,
  createQuestion,
  fetchQuestionById,
  updateQuestion,
  deleteQuestion,
  detectDuplicateQuestions,
  
  // Industries
  fetchIndustries,
  createIndustry,
  updateIndustry,
  deleteIndustry,
  
  // Sub-Industries
  fetchSubIndustries,
  createSubIndustry,
  updateSubIndustry,
  deleteSubIndustry,
  
  // Stats
  fetchAdminStats,
};
